import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Chip,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Close as UnlinkIcon,
  ToggleOn as ActivateIcon,
  ToggleOff as DeactivateIcon,
} from '@mui/icons-material';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import productsService, {
  type SupplierPlantDetail,
  type SupplierPlantProductItem,
  type ProductListItem,
} from '../services/products';
import { useAuth } from '../context/AuthContext';
import { hasPermission } from '../utils/userPermissions';
import { collapseProductRevisions } from '../utils/products';

const plantIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" width="25" height="41" viewBox="0 0 25 41">
      <path d="M12.5 0C5.6 0 0 5.6 0 12.5C0 21.9 12.5 41 12.5 41S25 21.9 25 12.5C25 5.6 19.4 0 12.5 0Z" fill="#6B9A1F" stroke="#FFFFFF" stroke-width="1.5"/>
      <circle cx="12.5" cy="12.5" r="6" fill="white"/>
    </svg>
  `),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

/** Audit timestamps are stored as naive UTC — parse them as UTC and render
 *  in the browser's local time (see timezone convention in datetime.ts). */
const formatDateTime = (iso?: string | null): string => {
  if (!iso) return '—';
  const hasZone = /Z|[+-]\d{2}:?\d{2}$/.test(iso);
  const d = new Date(hasZone ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDate = (iso?: string | null): string => {
  if (!iso) return '—';
  const hasZone = /Z|[+-]\d{2}:?\d{2}$/.test(iso);
  const d = new Date(hasZone ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
};

const FieldRow: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', gap: 2, py: 0.75 }}>
    <Typography variant="body2" color="text.secondary" sx={{ width: 160, flexShrink: 0 }}>
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={500}>
      {value || '—'}
    </Typography>
  </Box>
);

const BatchingPlantDetail: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { plantId } = useParams<{ plantId: string }>();
  const { user } = useAuth();
  const canManage = hasPermission(user, 'batch_plants.edit');

  const [plant, setPlant] = useState<SupplierPlantDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [error, setError] = useState<string>('');
  const [snackbar, setSnackbar] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  // Add-mix dialog
  const [addOpen, setAddOpen] = useState(false);
  const [allProducts, setAllProducts] = useState<ProductListItem[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [mixSearch, setMixSearch] = useState('');
  const [linkingId, setLinkingId] = useState<string | null>(null);

  // Unlink confirmation
  const [unlinkTarget, setUnlinkTarget] = useState<SupplierPlantProductItem | null>(null);
  const [unlinking, setUnlinking] = useState(false);

  const fetchPlant = useCallback(async () => {
    if (!plantId) return;
    setLoading(true);
    setError('');
    try {
      const data = await productsService.getSupplierPlant(plantId);
      setPlant(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load batching plant');
    } finally {
      setLoading(false);
    }
  }, [plantId]);

  useEffect(() => {
    fetchPlant();
  }, [fetchPlant]);

  const handleToggleActive = async () => {
    if (!plant) return;
    const nextActive = !(plant.is_active !== false);
    setToggling(true);
    try {
      const updated = await productsService.updateSupplierPlant(plant.supplier_plant_id, {
        is_active: nextActive,
      });
      setPlant({ ...plant, ...updated, products: plant.products });
      setSnackbar({
        msg: nextActive
          ? t('batchingPlant.activated', 'Plant activated')
          : t('batchingPlant.deactivated', 'Plant deactivated'),
        severity: 'success',
      });
    } catch (e: any) {
      setSnackbar({
        msg: e.response?.data?.detail || t('batchingPlant.toggleFailed', 'Failed to update plant status'),
        severity: 'error',
      });
    } finally {
      setToggling(false);
    }
  };

  const handleOpenAdd = async () => {
    setAddOpen(true);
    setMixSearch('');
    setProductsLoading(true);
    try {
      const data = await productsService.listProducts();
      setAllProducts(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setSnackbar({
        msg: e.response?.data?.detail || t('batchingPlant.loadMixesFailed', 'Failed to load mix designs'),
        severity: 'error',
      });
    } finally {
      setProductsLoading(false);
    }
  };

  const linkedIds = useMemo(
    () => new Set((plant?.products ?? []).map((p) => p.product_id)),
    [plant]
  );

  const availableProducts = useMemo(() => {
    // Collapse per-revision rows to the newest revision before filtering out
    // mixes already linked to this plant.
    let result = collapseProductRevisions(
      allProducts,
      (p) => `${p.code || ''} — ${p.description || ''}`,
    ).filter((p) => !linkedIds.has(p.product_id));
    if (mixSearch.trim()) {
      const q = mixSearch.toLowerCase();
      result = result.filter(
        (p) =>
          p.code?.toLowerCase().includes(q) ||
          p.description?.toLowerCase().includes(q)
      );
    }
    return result;
  }, [allProducts, linkedIds, mixSearch]);

  const handleLink = async (product: ProductListItem) => {
    if (!plant) return;
    setLinkingId(product.product_id);
    try {
      const item = await productsService.linkSupplierPlantProduct(
        plant.supplier_plant_id,
        product.product_id
      );
      setPlant({ ...plant, products: [...plant.products, item] });
      setSnackbar({
        msg: t('batchingPlant.mixLinked', 'Mix design linked to plant'),
        severity: 'success',
      });
    } catch (e: any) {
      setSnackbar({
        msg: e.response?.data?.detail || t('batchingPlant.linkFailed', 'Failed to link mix design'),
        severity: 'error',
      });
    } finally {
      setLinkingId(null);
    }
  };

  const handleUnlink = async () => {
    if (!plant || !unlinkTarget) return;
    setUnlinking(true);
    try {
      await productsService.unlinkSupplierPlantProduct(
        plant.supplier_plant_id,
        unlinkTarget.product_id
      );
      setPlant({
        ...plant,
        products: plant.products.filter((p) => p.product_id !== unlinkTarget.product_id),
      });
      setSnackbar({
        msg: t('batchingPlant.mixUnlinked', 'Mix design unlinked from plant'),
        severity: 'success',
      });
      setUnlinkTarget(null);
    } catch (e: any) {
      setSnackbar({
        msg: e.response?.data?.detail || t('batchingPlant.unlinkFailed', 'Failed to unlink mix design'),
        severity: 'error',
      });
    } finally {
      setUnlinking(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !plant) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/batching-plants')}
          sx={{ textTransform: 'none', mb: 2 }}
        >
          {t('common.back', 'Back')}
        </Button>
        <Alert severity="error">
          {error || t('batchingPlant.notFound', 'Batching plant not found')}
        </Alert>
      </Box>
    );
  }

  const active = plant.is_active !== false;
  const hasGps =
    plant.latitude && plant.longitude &&
    !Number.isNaN(Number(plant.latitude)) && !Number.isNaN(Number(plant.longitude));
  const position: [number, number] | null = hasGps
    ? [Number(plant.latitude), Number(plant.longitude)]
    : null;

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1.5} mb={2} flexWrap="wrap">
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/batching-plants')}
          sx={{ textTransform: 'none' }}
        >
          {t('common.back', 'Back')}
        </Button>
        <Typography variant="h6">{plant.name}</Typography>
        <Chip
          size="small"
          label={active ? t('batchingPlant.statusActive', 'Active') : t('batchingPlant.statusInactive', 'De-Active')}
          color={active ? 'success' : 'default'}
        />
        <Box sx={{ flexGrow: 1 }} />
        {canManage && (
          <>
            <Button
              variant="outlined"
              size="small"
              startIcon={toggling ? <CircularProgress size={16} /> : active ? <DeactivateIcon /> : <ActivateIcon />}
              onClick={handleToggleActive}
              disabled={toggling}
              color={active ? 'warning' : 'success'}
              sx={{ textTransform: 'none' }}
            >
              {active
                ? t('batchingPlant.deactivate', 'Deactivate')
                : t('batchingPlant.activate', 'Activate')}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/batching-plants/${plant.supplier_plant_id}/edit`)}
              sx={{ textTransform: 'none' }}
            >
              {t('batchingPlant.editPlant', 'Edit Batch Plant')}
            </Button>
          </>
        )}
      </Box>

      {!active && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('batchingPlant.inactiveBanner', 'This batching plant is deactivated and hidden from active lists.')}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Main column */}
        <Grid size={{ xs: 12, md: 9 }}>
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} mb={1}>
              {t('batchingPlant.detailsTitle', 'Plant Details')}
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: position ? 5 : 12 }}>
                <FieldRow label={t('batchingPlant.plantName', 'Plant Name')} value={plant.name} />
                <FieldRow
                  label={t('batchingPlant.readyMixSupplier', 'Ready-Mix Supplier')}
                  value={plant.ready_mix_supplier}
                />
                <FieldRow label={t('batchingPlant.address', 'Address')} value={plant.address} />
                <FieldRow
                  label={t('batchingPlant.gpsLocation', 'GPS Location')}
                  value={position ? `${plant.latitude}, ${plant.longitude}` : null}
                />
              </Grid>
              {position && (
                <Grid size={{ xs: 12, sm: 7 }}>
                  <Box sx={{ height: 240, borderRadius: 1, overflow: 'hidden' }}>
                    <MapContainer
                      center={position}
                      zoom={13}
                      style={{ height: '100%', width: '100%' }}
                      scrollWheelZoom={false}
                      attributionControl={false}
                    >
                      <TileLayer attribution="" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                      <Marker position={position} icon={plantIcon} />
                    </MapContainer>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Paper>

          <Paper elevation={2} sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ flexGrow: 1 }}>
                {t('batchingPlant.linkedMixes', 'Mix Designs / Recipes')} ({plant.products.length})
              </Typography>
              {canManage && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleOpenAdd}
                  sx={{ textTransform: 'none' }}
                >
                  {t('batchingPlant.addMix', 'Add Mix Design / Recipe')}
                </Button>
              )}
            </Box>
            {plant.products.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t('batchingPlant.noLinkedMixes', 'No mixes are linked to this plant yet.')}
              </Typography>
            ) : (
              <Box
                sx={{
                  overflowX: 'auto',
                  maxHeight: 340,
                  overflowY: 'auto',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 2,
                }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: 'text.secondary', fontWeight: 400 }}>
                        {t('batchingPlant.mixCode', 'Mix / Recipe Code')}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontWeight: 400 }}>
                        {t('batchingPlant.mixDescription', 'Description')}
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                        {t('batchingPlant.strengthGrade', 'Strength Grade')}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontWeight: 400 }}>
                        {t('batchingPlant.rev', 'Rev')}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontWeight: 400 }}>
                        {t('batchingPlant.dateLinked', 'Date Linked')}
                      </TableCell>
                      {canManage && <TableCell align="center" width={56} />}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {plant.products.map((p) => (
                      <TableRow
                        key={p.product_id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/products/${p.product_id}`)}
                      >
                        <TableCell sx={{ border: 0 }}>{p.code || '—'}</TableCell>
                        <TableCell sx={{ border: 0 }}>{p.description || '—'}</TableCell>
                        <TableCell align="right" sx={{ border: 0 }}>{p.strength_grade ?? '—'}</TableCell>
                        <TableCell sx={{ border: 0 }}>{p.rev || '—'}</TableCell>
                        <TableCell sx={{ border: 0 }}>{formatDate(p.date_linked)}</TableCell>
                        {canManage && (
                          <TableCell align="center" sx={{ border: 0 }}>
                            <Tooltip title={t('batchingPlant.unlinkMix', 'Unlink from plant')}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setUnlinkTarget(p);
                                }}
                              >
                                <UnlinkIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Tracking sidebar — same pattern as the project summary page */}
        <Grid size={{ xs: 12, md: 3 }}>
          <Paper elevation={2} sx={{ p: 2.5, height: '100%' }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                {t('batchingPlant.dateCreated', 'Date Created')}:
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {formatDateTime(plant.date_create)}
                {plant.create_by ? ` (${plant.create_by})` : ''}
              </Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                {t('batchingPlant.dateLastChanged', 'Date Last Changed')}:
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {formatDateTime(plant.date_last_modified)}
                {plant.modified_by ? ` (${plant.modified_by})` : ''}
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="caption" color="text.secondary">
              {t('batchingPlant.history', 'History')}
            </Typography>
            <Box sx={{ mt: 1.5 }}>
              {[
                {
                  label: t('batchingPlant.historyCreated', 'Plant Created'),
                  when: plant.date_create,
                  who: plant.create_by,
                },
                {
                  label: t('batchingPlant.historyChanged', 'Last Changed'),
                  when: plant.date_last_modified,
                  who: plant.modified_by,
                },
              ].map((ev, i, arr) => (
                <Box key={ev.label} sx={{ display: 'flex', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: 'success.main',
                        mt: 0.5,
                      }}
                    />
                    {i < arr.length - 1 && (
                      <Box sx={{ width: '2px', flex: 1, bgcolor: 'grey.300', my: 0.5 }} />
                    )}
                  </Box>
                  <Box sx={{ pb: 2 }}>
                    <Typography variant="body2" fontWeight={600}>{ev.label}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {formatDateTime(ev.when)}
                    </Typography>
                    {ev.who && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {ev.who}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Add mix dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('batchingPlant.addMixTitle', 'Link a Mix Design / Recipe')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            size="small"
            autoFocus
            placeholder={t('batchingPlant.searchMixes', 'Search by code or description...')}
            value={mixSearch}
            onChange={(e) => setMixSearch(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          {productsLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={28} />
            </Box>
          ) : availableProducts.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              {allProducts.length > 0 && linkedIds.size >= allProducts.length
                ? t('batchingPlant.allMixesLinked', 'All mix designs are already linked to this plant.')
                : t('batchingPlant.noMixesFound', 'No mix designs found.')}
            </Typography>
          ) : (
            <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('batchingPlant.mixCode', 'Mix / Recipe Code')}</TableCell>
                    <TableCell>{t('batchingPlant.mixDescription', 'Description')}</TableCell>
                    <TableCell align="right" width={90} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {availableProducts.map((p) => (
                    <TableRow key={p.product_id} hover>
                      <TableCell>{p.code || '—'}</TableCell>
                      <TableCell>{p.description || '—'}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={linkingId !== null}
                          startIcon={linkingId === p.product_id ? <CircularProgress size={14} /> : undefined}
                          onClick={() => handleLink(p)}
                          sx={{ textTransform: 'none' }}
                        >
                          {t('batchingPlant.link', 'Link')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)} sx={{ textTransform: 'none' }}>
            {t('common.close', 'Close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unlink confirmation */}
      <Dialog open={unlinkTarget !== null} onClose={() => !unlinking && setUnlinkTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('batchingPlant.unlinkTitle', 'Unlink mix design?')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t(
              'batchingPlant.unlinkConfirm',
              '"{{code}}" will no longer be linked to this batching plant. The mix design itself is not deleted.',
              { code: unlinkTarget?.code || unlinkTarget?.description || '' },
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnlinkTarget(null)} disabled={unlinking} sx={{ textTransform: 'none' }}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleUnlink}
            disabled={unlinking}
            startIcon={unlinking ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{ textTransform: 'none' }}
          >
            {t('batchingPlant.unlink', 'Unlink')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar !== null}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
      >
        <Alert
          severity={snackbar?.severity ?? 'success'}
          onClose={() => setSnackbar(null)}
          variant="filled"
        >
          {snackbar?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BatchingPlantDetail;
